import React, { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ConfigProvider
} from 'antd'
import {
  antdLocale
} from '@/locales'

import { deleteTokenApi } from '@/services/login'
import requireAll from '@/utils/requireAll'
import login from '@/utils/login'
import user from '@/utils/user'
import cache from '@/utils/cache'
import favicon from '@/assets/images/favicon.png'
import MarqueeMessage from '@/components/marqueeMessage'
import { debounce } from 'lodash'
import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import AuthGuard from '@/components/authGuard'

const allRoutersMap = requireAll(require.context('@/routers/', false, process.env.NODE_ENV === 'development' ? /(?<!index)\.js$/ : /(?<!(index|demo))\.js$/))
export const allRouters = Object.keys(allRoutersMap).reduce((total, currKey, index) => {
  const currVal = allRoutersMap[currKey]
  return total.concat(currVal)
}, [])

const mobile = window.screen.width < 768

const App = (props) => {
  const location = useLocation()

  // 更改favicon
  const changeFavicon = useCallback((link) => {
    let $favicon = window.document.querySelector('link[rel="shortcut icon"]')
    if ($favicon !== null) {
      $favicon.href = link
    } else {
      $favicon = document.createElement('link')
      $favicon.rel = 'shortcut icon'
      $favicon.href = link
      document.head.appendChild($favicon)
    }
  }, [])

  // 获取自定义配置（租户）
  const getCustomization = useCallback(() => {
    let customization = JSON.parse(localStorage.getItem('customization'))
    if (customization) {
      let { platformName: pName, addressBarLogo: logo, customizationEnable } = customization
      if (customizationEnable) {
        const name = pName || '云盯360'
        const logoUrl = logo || favicon
        window.document.title = name
        changeFavicon(logoUrl)
      } else {
        const name = '云盯360'
        const logoUrl = favicon
        window.document.title = name
        changeFavicon(logoUrl)
      }
    }
  }, [changeFavicon])

  const handleResize = useCallback(() => {
    const defaultFontSize = 16
    let newScale = 1
    const windowScale = document.documentElement.clientWidth / 1920 || 1
    newScale *= windowScale
    document.documentElement.style.fontSize = `${defaultFontSize * newScale}px`
  }, [])

  const onload = useCallback(() => {
    let time = ''
    let numbers = window.localStorage.getItem('numbers')
    const sesTime = window.sessionStorage.getItem('sesTime')
    const localTime = window.localStorage.getItem('localTime')
    if (numbers === 'NaN' || numbers === null || parseInt(numbers, 10) < 0) {
      numbers = 0
    }
    if ((sesTime === null || sesTime === 'NaN' || localTime === null || localTime === 'NaN') && parseInt(numbers, 10) <= 0) {
      window.localStorage.removeItem('localTime')
      window.sessionStorage.removeItem('sesTime')
      return deleteTokenApi().finally(_ => {
        login.logout()
        user.remove()
        time = new Date().getTime()
        window.sessionStorage.setItem('sesTime', time)
        window.localStorage.setItem('localTime', time)
      })
    }
    if (!time && localTime !== 'NaN' && localTime !== null && sesTime !== localTime) {
      window.sessionStorage.setItem('sesTime', localTime)
    }
    if (allRouters.some(v => v.path === location.pathname) || location.pathname === '/') {
      numbers = parseInt(numbers, 10) + 1
    }
    window.localStorage.setItem('numbers', numbers)
  }, [location.pathname])

  useEffect(() => {
    window.onload = onload()
    getCustomization()
    cache.localS('nowpathname', location.pathname)
    if (!localStorage.getItem('lang')) {
      localStorage.setItem('lang', 'zh-CN')
    }
    handleResize()
    const debouncedResize = debounce(handleResize, 100)
    window.addEventListener('resize', debouncedResize)
    return () => {
      window.removeEventListener('resize', debouncedResize)
    }
  }, [])

  useEffect(() => {
    cache.localS('nowpathname', location.pathname)
  }, [location.pathname])

  return (
    <ConfigProvider
      locale={antdLocale}
      getPopupContainer={trigger => trigger ? trigger.parentNode : document.body}
    >
      {/* 
          hashPriority="high" 降权:where选择器,避免旧版本浏览器兼容问题;
          legacyLogicalPropertiesTransformer  降级css逻辑属性 inset:0 =>  top:0;right:0;bottom:0;left:0; 旧版本浏览器兼容性不好
      */}
      <StyleProvider
        hashPriority='high'
        transformers={[legacyLogicalPropertiesTransformer]}
      >
        <div id="App">
          {
            !mobile && <MarqueeMessage />
          }
          {props.children}
          <AuthGuard />
        </div>
      </StyleProvider>

    </ConfigProvider>
  )
}

export default App